import { Field, ImageField, RichText, Text } from '@sitecore-jss/sitecore-jss-nextjs';
import React from 'react';
import { IconWithTitle } from '../shared/IconWithTitle';
import {
  StCenteredBox,
  StContainer,
  StDivider,
  StInfoStepItem,
  StInfoStepsContainer,
  StTypography,
  StInformationContainer,
} from './InformationalSteps.style';
import InformationalBlock from '../shared/InformationalBlocks/InformationalBlock';
import { useTheme } from '@mui/material/styles';
import {
  StDTPGridContainer,
  StFooterGridItemLeft,
  StFooterGridItemRight,
} from '../shared/Container/Container.styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import UiSubHeading from '../../components/shared/Typography/UiSubHeading';
import UiText from '../../components/shared/Typography/UiText';
import useApplyV2Theme from '../../lib/hooks/useApplyV2Theme';

// Define all possible variant types
type VariantType =
  | 'HorizontalVariation1'
  | 'HorizontalVariation3'
  | 'HorizontalVariation4'
  | 'VerticalVariation2'
  | 'VerticalVariation5'
  | 'VerticalVariation6';

// Constant for variant strings
const VARIANTS = {
  H1: 'HorizontalVariation1',
  H3: 'HorizontalVariation3',
  H4: 'HorizontalVariation4',
  V2: 'VerticalVariation2',
  V5: 'VerticalVariation5',
  V6: 'VerticalVariation6',
} as const;

type InfoStep = {
  fields: {
    stepIcon: any;
    stepNumber: Field<number>;
    stepTitle: Field<string>;
    stepDescription: Field<string>;
  };
};

type InformationalStepsProps = {
  fields: {
    infoStepsList: InfoStep[];
    subheading: Field<string>;
    showHighlightedUnderline: Field<boolean>;
    underlineColor: Field<string>;
    hideStepNumbers: Field<boolean>;
    makeInvertedBackground: Field<boolean>;
    infoStepsVariantType: { value: string };
    title: Field<string>;
    summarySection1?: Field<string>;
    summarySection2?: Field<string>;
    titleIcon: ImageField;
    iconWithTitleVariant: { value: string };
  };
};

const InformationalSteps = (props: InformationalStepsProps): JSX.Element => {
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const { isToapplyV2Theme } = useApplyV2Theme();
  if (!props?.fields) return <></>;

  const makeInvertedBackground = !!props?.fields?.makeInvertedBackground?.value;

  const variant: VariantType =
    (props?.fields?.infoStepsVariantType?.value as VariantType) || VARIANTS.H1;

  const showUnderlineColor = variant === VARIANTS.H4;

  const showTextBottom = ([VARIANTS.H3, VARIANTS.H4] as VariantType[]).includes(variant);
  const isVertical = ([VARIANTS.V5, VARIANTS.V6] as VariantType[]).includes(variant);

  const { summarySection1, summarySection2, titleIcon, title, iconWithTitleVariant } =
    props?.fields;

  const alignItems = variant === VARIANTS.V2 ? 'center' : 'flex-start';

  const calculateBreakpoints = (v: VariantType) => {
    switch (v) {
      case VARIANTS.V2:
        return { lg: 6 };
      case VARIANTS.H3:
      case VARIANTS.H4:
        return { lg: 2.4 };
      case VARIANTS.H1:
        return { lg: 4 };
      default:
        return { lg: 12 };
    }
  };

  const breakpoints = calculateBreakpoints(variant);

  const IconWithTitleProps = {
    titleIcon: titleIcon,
    title: title,
    iconWithTitleVariant: iconWithTitleVariant.value,
    invertColor: makeInvertedBackground,
  };

  const RenderFooterText = () => {
    return (
      <StDTPGridContainer display="flex" justifyContent="space-evenly" alignItems="start">
        {summarySection1?.value && (
          <StFooterGridItemRight isTablet={isTablet}>
            {isToapplyV2Theme ? (
              <UiText>{summarySection1?.value}</UiText>
            ) : (
              <RichText field={summarySection1} />
            )}
          </StFooterGridItemRight>
        )}

        {summarySection2?.value && (
          <StFooterGridItemLeft isTablet={isTablet}>
            {isToapplyV2Theme ? (
              <UiText>{summarySection2?.value}</UiText>
            ) : (
              <RichText field={summarySection2} />
            )}
          </StFooterGridItemLeft>
        )}
      </StDTPGridContainer>
    );
  };

  return (
    <StInformationContainer invert={makeInvertedBackground} id="informational-steps">
      <StCenteredBox>
        <StContainer id="informational-steps-container">
          <IconWithTitle {...IconWithTitleProps} />
          {props?.fields?.subheading &&
            (isToapplyV2Theme ? (
              <UiSubHeading
                color="primary"
                align="center"
                styles={{ fontSize: '18px', fontWeight: 700, margin: '0 0 16px' }}
              >
                <RichText field={props?.fields?.subheading} />
              </UiSubHeading>
            ) : (
              <StTypography invert={makeInvertedBackground} sx={{ marginBlock: '1rem' }}>
                <Text field={props?.fields?.subheading} />
              </StTypography>
            ))}
          {showUnderlineColor && <StDivider />}
          <StInfoStepsContainer container spacing={2}>
            {props?.fields?.infoStepsList?.map((step, index) => (
              <StInfoStepItem key={index} item lgWidth={breakpoints.lg} xs={12} sm={12} md={12}>
                <InformationalBlock
                  stepNumber={step?.fields?.stepNumber}
                  stepTitle={step?.fields?.stepTitle}
                  stepDescription={step?.fields?.stepDescription}
                  hideStepNumbers={props?.fields?.hideStepNumbers}
                  infoStepsVariantType={variant}
                  showTextBottom={showTextBottom}
                  alignItems={alignItems}
                  isVertical={isVertical}
                  invert={makeInvertedBackground}
                  stepIcon={step?.fields?.stepIcon?.value}
                />
              </StInfoStepItem>
            ))}
          </StInfoStepsContainer>
        </StContainer>
        {(summarySection1?.value || summarySection2?.value) && <RenderFooterText />}
      </StCenteredBox>
    </StInformationContainer>
  );
};

export default InformationalSteps;
